import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useRecaptchaGuestSession } from '@/features/guest-session/hooks/useRecaptchaGuestSession';
import { useCareConciergeModal } from '../hooks/useCareConciergeModal';
import {
  submitCareConciergeRequest,
  submitGuestCareConciergeRequest,
} from '@/features/provider-directory/api/contactApi';
import {
  CareConciergeFormData,
  PageOrigin,
} from '@/features/provider-directory/components/CareConciergContactForm/types';
import { trackRumAction } from '@/shared/observability/rum';
import CareSeekerWorkflow from '@/features/provider-directory/components/CareConciergContactForm/CareSeekerWorkflow';
import { Button } from '@/shared/components/ui/button';
import { CheckCircle, AlertCircle, Info, Loader2 } from 'lucide-react';

type CareConciergeFormProps = {
  onSuccess: () => void;
  onClose: () => void;
  /** Feature 104: entry-point origin; defaults to the pre-104 value. */
  pageOrigin?: PageOrigin;
};

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export function CareConciergeForm({
  onSuccess,
  onClose,
  pageOrigin = 'care-seeker-help-getting-started',
}: CareConciergeFormProps) {
  const { user } = useAuth();
  const { recaptchaElement, ensureGuestSession } = useRecaptchaGuestSession();
  const { saveDraft, getDraft, clearDraft } = useCareConciergeModal(user?.id ?? 'guest');
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [requestId, setRequestId] = useState<string>('');
  const [showDraftBanner, setShowDraftBanner] = useState<boolean>(false);
  const [draftTimestamp, setDraftTimestamp] = useState<number>(0);

  // Initialize form with default values
  const draft = getDraft();
  const defaultValues: Partial<CareConciergeFormData> = {
    requestorType: 'LOVED_ONE',
    requestorName: user ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}` : '',
    requestorEmail: user?.email || '',
    requestorPhone: '',
    ...(draft?.data || {}),
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<CareConciergeFormData>({
    defaultValues,
  });

  // Check for draft on mount
  useEffect(() => {
    const existingDraft = draft;
    if (existingDraft && existingDraft.data && Object.keys(existingDraft.data).length > 0) {
      setShowDraftBanner(true);
      setDraftTimestamp(existingDraft.timestamp);
    }
  }, [draft]); // Run when draft changes

  // Watch form values and auto-save (debounced)
  const formValues = watch();
  const urgencyLevel = watch('urgencyLevel');
  const relationshipToRecipient = watch('relationshipToRecipient');
  const needsSameLocationRaw = watch('needsSameLocation');

  useEffect(() => {
    // Only auto-save if form has been touched and not submitted
    if (submitStatus === 'idle') {
      const timeoutId = setTimeout(() => {
        saveDraft(formValues);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, submitStatus, saveDraft]); // saveDraft is stable from useCallback

  const onSubmit = async (data: CareConciergeFormData) => {
    setSubmitStatus('loading');
    setErrorMessage('');

    try {
      // Ensure requestorType is set
      const categoriesRaw = data.categories;
      const categoriesArray = Array.isArray(categoriesRaw)
        ? categoriesRaw
        : typeof categoriesRaw === 'string' && (categoriesRaw as string).trim()
          ? (categoriesRaw as string).split(',').map((s: string) => s.trim()).filter(Boolean)
          : undefined;

      const submitData = {
        ...data,
        requestorType: 'LOVED_ONE' as const,
        pageOrigin,
        categories: categoriesArray,
      };

      // Guests submit through the public guest endpoint, authorized by a
      // guest-session token (minted behind reCAPTCHA). ensureGuestSession is a
      // no-op for logged-in users and when a valid token already exists (e.g.
      // from the navigator chat the guest just completed).
      await ensureGuestSession(Boolean(user));
      const response = user
        ? await submitCareConciergeRequest(submitData)
        : await submitGuestCareConciergeRequest(submitData);

      setSubmitStatus('success');
      setRequestId(response.id);
      clearDraft();
      setShowDraftBanner(false);
      onSuccess();

      // Feature 104: funnel event (works for guests too — no auth gate)
      trackRumAction('concierge_form_submitted', {
        source: pageOrigin,
        requestorType: 'LOVED_ONE',
        urgencyLevel: data.urgencyLevel,
      });
    } catch (error) {
      setSubmitStatus('error');
      const errorMsg = error instanceof Error ? error.message : 'Failed to submit request. Please try again.';
      setErrorMessage(errorMsg);

      trackRumAction('concierge_form_error', { source: pageOrigin });
    }
  };

  const handleClearDraft = () => {
    clearDraft();
    setShowDraftBanner(false);
    reset({
      requestorType: 'LOVED_ONE',
      requestorName: user ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}` : '',
      requestorEmail: user?.email || '',
      requestorPhone: '',
    });

  };

  const formatDraftDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Show success state
  if (submitStatus === 'success') {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-recoleta font-semibold text-[#111827] mb-2">
          Request Submitted Successfully!
        </h3>
        {requestId && (
          <p className="text-gray-500 font-termina text-xs mb-4">Request ID: {requestId}</p>
        )}
        <p className="text-base text-[#111827] font-termina mb-4">
          A member of bQuest's team will review your request and contact you within 3 business days to help connect you with the appropriate care provider.
        </p>

        <div className="bg-white p-4 rounded-lg border border-[#026857]/20 mb-6 text-left">
          <h5 className="font-termina font-semibold text-[#026857] mb-3">
            The bQuest Care Concierge Process: What you can expect from us
          </h5>

          <div className="space-y-4">
            <div>
              <h6 className="font-termina font-semibold text-[#111827] mb-1">
                Initial Update / Shortlist Delivery: Within 3 business days (72 hours)
              </h6>
              <p className="text-sm text-[#4B5563] font-termina mb-2">
                You will receive:
              </p>
              <ul className="list-disc list-inside text-sm text-[#4B5563] font-termina space-y-1 ml-2">
                <li>A shortlist of qualified providers identified through extended network search</li>
                <li>Or status update if more time is needed, specifying when to expect final matches</li>
              </ul>
              <p className="text-sm text-[#4B5563] font-termina mt-2 italic">
                For higher-complexity or rare specialties, we will communicate proactively about how we're engaging our national network of providers.
              </p>
            </div>

            <div>
              <h6 className="font-termina font-semibold text-[#111827] mb-1">
                Final Sourcing & Introduction: Within 5–7 business days
              </h6>
              <p className="text-sm text-[#4B5563] font-termina mb-2">
                You will receive:
              </p>
              <ul className="list-disc list-inside text-sm text-[#4B5563] font-termina space-y-1 ml-2">
                <li>1–3 vetted provider options, or</li>
                <li>Confirmation that no qualified providers currently meet your vetting standards, along with a plan for follow-up</li>
              </ul>
              <p className="text-sm text-[#4B5563] font-termina mt-2 italic">
                For ultra-specific or rural requests, timeline may extend to 10 business days, but this will be rare and clearly communicated upfront.
              </p>
            </div>
          </div>
        </div>

        <Button
          onClick={onClose}
          className="bg-[#026857] hover:bg-[#025045] text-white"
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Invisible reCAPTCHA — only used for guests without a valid session token. */}
      {recaptchaElement}

      {/* Draft Restoration Banner */}
      {showDraftBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Info className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
              <span className="text-sm text-blue-800 font-termina">
                Draft restored from {formatDraftDate(draftTimestamp)}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearDraft}
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
            >
              Clear Draft
            </Button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-red-800 font-termina font-semibold mb-1">Submission Failed</h4>
              <p className="text-red-700 font-termina text-sm mb-3">{errorMessage}</p>
              <Button
                type="button"
                onClick={() => setSubmitStatus('idle')}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Care Seeker Workflow Form */}
      <CareSeekerWorkflow
        register={register}
        errors={errors}
        urgencyLevel={urgencyLevel}
        relationshipToRecipient={relationshipToRecipient}
        needsSameLocation={
          typeof needsSameLocationRaw === 'string'
            ? needsSameLocationRaw === 'true'
            : needsSameLocationRaw
        }
        requestorType="LOVED_ONE"
      />

      {/* Submit Button */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={submitStatus === 'loading'}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitStatus === 'loading'}
          className="bg-[#E2A807] hover:bg-[#C99006] text-white"
        >
          {submitStatus === 'loading' ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Request'
          )}
        </Button>
      </div>
    </form>
  );
}
